import { Compass, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { CreateModelRequest, ModelRecord } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCreateModel,
  useDeleteModel,
  useDelistModel,
  useListModel,
  useModelsList,
} from '../api/useModels.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Tabs,
  TabsList,
  TabsTrigger,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  buttonClass,
} from '../ui/index.ts';

/**
 * `/models` list view — Dispatch Deck (epic #835). Dense catalog table
 * with the standard `ui/Table` chrome, shelf-status tabs, and a themed
 * vendor filter.
 *
 * Operator affordances:
 *   - Client-side search across vendor / model id / display name.
 *   - Vendor filter sits above the table so the row itself can stay compact.
 *   - Vendor discovery creates delisted candidates; this page lists and
 *     shelves those candidates instead of registering model ids by hand.
 *   - Per-row Manage (navigates to detail) + Delete (soft-delete confirm).
 */

const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 6,
});

function formatUsdPerMtok(value: number): string {
  return PRICE_FORMATTER.format(value);
}

function formatOptionalUsdPerMtok(value: number | null | undefined, fallback: string): string {
  return value === null || value === undefined ? fallback : formatUsdPerMtok(value);
}

function isAnthropicVendor(vendor: string): boolean {
  return vendor.toLowerCase() === 'anthropic';
}

function isOpenAiVendor(vendor: string): boolean {
  return vendor.toLowerCase() === 'openai';
}

interface PriceMetric {
  readonly id: string;
  readonly label: I18nKey;
  readonly value: string;
}

function buildPriceMetrics(row: ModelRecord, notConfiguredLabel: string): PriceMetric[] {
  const pricing = row.current_pricing;
  if (pricing === null || pricing === undefined) {
    return [];
  }
  if (isAnthropicVendor(row.vendor)) {
    return [
      {
        id: 'input',
        label: 'models.price.input',
        value: formatUsdPerMtok(pricing.input_per_mtok_usd),
      },
      {
        id: 'cache-read',
        label: 'models.price.cache_read',
        value: formatUsdPerMtok(pricing.cache_read_per_mtok_usd),
      },
      {
        id: 'cache-write-5m',
        label: 'models.price.cache_write_5m',
        value: formatOptionalUsdPerMtok(
          pricing.cache_write_5m_per_mtok_usd ?? pricing.cache_write_per_mtok_usd,
          notConfiguredLabel,
        ),
      },
      {
        id: 'cache-write-1h',
        label: 'models.price.cache_write_1h',
        value: formatOptionalUsdPerMtok(pricing.cache_write_1h_per_mtok_usd, notConfiguredLabel),
      },
      {
        id: 'output',
        label: 'models.price.output',
        value: formatUsdPerMtok(pricing.output_per_mtok_usd),
      },
    ];
  }
  if (isOpenAiVendor(row.vendor)) {
    return [
      {
        id: 'input',
        label: 'models.price.input',
        value: formatUsdPerMtok(pricing.input_per_mtok_usd),
      },
      {
        id: 'cache-hit',
        label: 'models.price.cache_hit',
        value: formatUsdPerMtok(pricing.cache_read_per_mtok_usd),
      },
      {
        id: 'cache-write',
        label: 'models.price.cache_write',
        value: pricing.cache_write_per_mtok_usd > 0
          ? formatUsdPerMtok(pricing.cache_write_per_mtok_usd)
          : notConfiguredLabel,
      },
      {
        id: 'output',
        label: 'models.price.output',
        value: formatUsdPerMtok(pricing.output_per_mtok_usd),
      },
    ];
  }
  return [
    {
      id: 'input',
      label: 'models.price.input',
      value: formatUsdPerMtok(pricing.input_per_mtok_usd),
    },
    {
      id: 'cache-read',
      label: 'models.price.cache_read',
      value: formatUsdPerMtok(pricing.cache_read_per_mtok_usd),
    },
    {
      id: 'cache-write',
      label: 'models.price.cache_write',
      value: formatUsdPerMtok(pricing.cache_write_per_mtok_usd),
    },
    {
      id: 'output',
      label: 'models.price.output',
      value: formatUsdPerMtok(pricing.output_per_mtok_usd),
    },
  ];
}

function priceModeLabelKey(row: ModelRecord): I18nKey {
  if (isAnthropicVendor(row.vendor)) {
    return 'models.price.mode.anthropic';
  }
  if (isOpenAiVendor(row.vendor)) {
    return (row.current_pricing?.cache_write_per_mtok_usd ?? 0) > 0
      ? 'models.price.mode.openai_write_billed'
      : 'models.price.mode.openai_hit_only';
  }
  return 'models.price.mode.legacy';
}

interface RowProps {
  readonly row: ModelRecord;
  readonly onDelete: (row: ModelRecord) => void;
  readonly onShelfToggle: (row: ModelRecord) => void;
  readonly deleting: boolean;
  readonly shelfing: boolean;
  readonly manageLabel: string;
  readonly deleteLabel: string;
  readonly listLabel: string;
  readonly delistLabel: string;
}

function ModelRow({
  row,
  onDelete,
  onShelfToggle,
  deleting,
  shelfing,
  manageLabel,
  deleteLabel,
  listLabel,
  delistLabel,
}: RowProps): JSX.Element {
  const t = useT();
  const idAttr = `${row.vendor}-${row.model}`;
  const detailPath = `/supply/models/${encodeURIComponent(row.vendor)}/${encodeURIComponent(row.model)}`;
  return (
    <Tr data-testid={`model-row-${idAttr}`}>
      <Td emphasis>
        <div className="flex min-w-[220px] flex-col gap-2">
          <Link
            to={detailPath}
            className="font-mono transition-colors duration-120 hover:text-signal focus-visible:underline focus-visible:outline-none"
          >
            {row.model}
          </Link>
        </div>
      </Td>
      <Td>{row.display_name ?? '-'}</Td>
      <Td>
        {row.current_pricing !== null && row.current_pricing !== undefined ? (
          <div
            className="flex min-w-[240px] flex-col gap-2"
            data-testid={`model-row-${idAttr}-price`}
          >
            <Chip
              tone="neutral"
              lamp={false}
              className="w-fit"
              testId={`model-row-${idAttr}-price-mode`}
            >
              {t(priceModeLabelKey(row))}
            </Chip>
            <dl className="m-0 grid grid-cols-[minmax(72px,auto)_minmax(56px,auto)] gap-x-3 gap-y-1 text-label">
              {buildPriceMetrics(
                row,
                t(isOpenAiVendor(row.vendor) ? 'models.price.not_billed' : 'models.price.not_configured'),
              ).map((metric) => (
                <div
                  key={metric.id}
                  className="contents"
                  data-testid={`model-row-${idAttr}-price-${metric.id}`}
                >
                  <dt className="text-paper-low">{t(metric.label)}</dt>
                  <dd className="m-0 font-mono font-medium text-paper-mid">{metric.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <Chip tone="neutral" lamp={false}>
            {t('models.price.unpriced')}
          </Chip>
        )}
      </Td>
      <Td>
        <div className="flex items-center justify-end gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onShelfToggle(row)}
            disabled={shelfing}
            data-testid={`model-row-${idAttr}-shelf`}
            className="text-signal hover:text-signal"
          >
            {row.shelf_status === 'listed' ? delistLabel : listLabel}
          </Button>
          <Link to={detailPath} data-testid={`model-row-${idAttr}-manage`} className={buttonClass('ghost', 'sm')}>
            {manageLabel}
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(row)}
            disabled={deleting}
            data-testid={`model-row-${idAttr}-delete`}
            className="text-bad hover:bg-bad-dim hover:text-bad"
          >
            {deleteLabel}
          </Button>
        </div>
      </Td>
    </Tr>
  );
}

interface DeleteTarget {
  readonly vendor: string;
  readonly model: string;
}

export function ModelsList(): JSX.Element {
  const t = useT();
  const list = useModelsList();
  const deleteMutation = useDeleteModel();
  const listMutation = useListModel();
  const delistMutation = useDelistModel();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [shelfTab, setShelfTab] = useState<'listed' | 'delisted'>('listed');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const vendorOptions = useMemo(() => {
    return Array.from(new Set((list.data ?? []).map((row) => row.vendor))).sort((a, b) =>
      a.localeCompare(b),
    );
  }, [list.data]);

  useEffect(() => {
    if (vendorFilter !== 'all' && !vendorOptions.includes(vendorFilter)) {
      setVendorFilter('all');
    }
  }, [vendorFilter, vendorOptions]);

  const vendorFilteredRows = useMemo(() => {
    const rows = list.data ?? [];
    if (vendorFilter === 'all') {
      return rows;
    }
    return rows.filter((row) => row.vendor === vendorFilter);
  }, [list.data, vendorFilter]);

  const shelfCounts = useMemo(() => {
    return {
      listed: vendorFilteredRows.filter((row) => row.shelf_status === 'listed').length,
      delisted: vendorFilteredRows.filter((row) => row.shelf_status === 'delisted').length,
    };
  }, [vendorFilteredRows]);

  const filtered = useMemo(() => {
    const rows = vendorFilteredRows.filter((row) => row.shelf_status === shelfTab);
    const query = search.trim().toLowerCase();
    if (query.length === 0) {
      return rows;
    }
    return rows.filter(
      (row) =>
        row.vendor.toLowerCase().includes(query) ||
        row.model.toLowerCase().includes(query) ||
        (row.display_name ?? '').toLowerCase().includes(query),
    );
  }, [search, shelfTab, vendorFilteredRows]);

  async function confirmDelete(): Promise<void> {
    if (deleteTarget === null) {
      return;
    }
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync({
        vendor: deleteTarget.vendor,
        model: deleteTarget.model,
      });
      setDeleteTarget(null);
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setDeleteError(message);
      toast.error(message);
    }
  }

  async function toggleShelf(row: ModelRecord): Promise<void> {
    try {
      if (row.shelf_status === 'listed') {
        await delistMutation.mutateAsync({
          vendor: row.vendor,
          model: row.model,
          reason: 'ops manual delist',
        });
        toast.success('模型已下架');
      } else {
        await listMutation.mutateAsync({
          vendor: row.vendor,
          model: row.model,
          reason: 'ops manual list',
        });
        toast.success('模型已上架');
      }
    } catch (err) {
      toast.error(coerceErrorMessage(err));
    }
  }

  return (
    <section data-testid="models-list-page" className="flex flex-col gap-6">
      <PageHeader
        title={t('models.title')}
        sub={t('models.subtitle')}
        actions={
          <>
            <div className="relative">
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder={t('models.search.placeholder')}
                aria-label={t('models.search.aria_label')}
                data-testid="models-search"
                className="w-72 pr-9"
              />
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-paper-low"
              />
            </div>
            <Button variant="secondary" onClick={() => setCreateOpen(true)} data-testid="models-create-open">
              <Plus aria-hidden="true" className="h-4 w-4" />
              {t('models.create.open')}
            </Button>
            <Link to="/supply/vendors" data-testid="models-discover" className={buttonClass('primary')}>
              <Compass aria-hidden="true" className="mr-1.5 h-4 w-4" />
              {t('models.new')}
            </Link>
          </>
        }
      />

      {list.isError ? (
        <div
          role="alert"
          data-testid="models-error"
          className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim px-4 py-3"
        >
          <div className="flex flex-col gap-1">
            <span className="text-body font-medium text-bad-bright">{t('models.error.title')}</span>
            <span className="text-dense text-bad-bright">
              {list.error instanceof OpsApiError
                ? `${list.error.code}: ${list.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <Button variant="secondary" size="sm" onClick={() => void list.refetch()} data-testid="models-retry">
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {list.isSuccess ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div data-testid="models-shelf-tabs" className="w-fit">
            <Tabs
              value={shelfTab}
              onValueChange={(next) => setShelfTab(next === 'delisted' ? 'delisted' : 'listed')}
            >
              <TabsList ariaLabel="模型上下架状态">
                {(['listed', 'delisted'] as const).map((status) => {
                  const label = status === 'listed' ? '已上架' : '已下架';
                  const count = shelfCounts[status];
                  return (
                    <TabsTrigger key={status} value={status} testId={`models-tab-${status}`}>
                      <span className="inline-flex items-center gap-2">
                        <span>{label}</span>
                        <span className="font-mono text-label">{count}</span>
                      </span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>
          <div className="flex w-full max-w-xs items-center gap-2 sm:w-auto">
            <span className="shrink-0 text-label text-paper-mid">{t('models.filter.vendor')}</span>
            <Select
              value={vendorFilter}
              onValueChange={setVendorFilter}
              options={[
                { value: 'all', label: t('models.filter.vendor_all') },
                ...vendorOptions.map((vendor) => ({ value: vendor, label: vendor })),
              ]}
              mono
              ariaLabel={t('models.filter.vendor')}
              testId="models-vendor-filter"
              className="min-w-44"
            />
          </div>
        </div>
      ) : null}

      {list.isPending ? (
        <div data-testid="models-loading" className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
          <span className="sr-only">{t('models.loading_aria')}</span>
        </div>
      ) : null}

      {list.isSuccess && filtered.length === 0 ? (
        <EmptyState
          testId="models-empty"
          title={search.length > 0 ? t('models.empty.search.title') : t('models.empty.initial.title')}
          hint={search.length > 0 ? t('models.empty.search.body') : t('models.empty.initial.body')}
        />
      ) : null}

      {list.isSuccess && filtered.length > 0 ? (
        <Table testId="models-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('models.column.model')}</Th>
              <Th>{t('models.column.display_name')}</Th>
              <Th>{t('models.column.price')}</Th>
              <Th className="text-right">{t('models.column.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {filtered.map((row) => (
              <ModelRow
                key={`${row.vendor}:${row.model}`}
                row={row}
                onShelfToggle={(target) => void toggleShelf(target)}
                onDelete={(target) => {
                  setDeleteError(null);
                  setDeleteTarget({ vendor: target.vendor, model: target.model });
                }}
                deleting={
                  deleteMutation.isPending &&
                  deleteMutation.variables?.vendor === row.vendor &&
                  deleteMutation.variables?.model === row.model
                }
                shelfing={
                  (listMutation.isPending || delistMutation.isPending) &&
                  ((listMutation.variables?.vendor === row.vendor &&
                    listMutation.variables?.model === row.model) ||
                    (delistMutation.variables?.vendor === row.vendor &&
                      delistMutation.variables?.model === row.model))
                }
                manageLabel={t('models.row.manage')}
                deleteLabel={t('models.row.delete')}
                listLabel="上架"
                delistLabel="下架"
              />
            ))}
          </TBody>
        </Table>
      ) : null}

      {deleteTarget !== null ? (
        <DeleteModelModal
          target={deleteTarget}
          busy={deleteMutation.isPending}
          error={deleteError}
          onCancel={() => {
            if (!deleteMutation.isPending) {
              setDeleteTarget(null);
              setDeleteError(null);
            }
          }}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}
      {createOpen ? (
        <CreateModelModal onClose={() => setCreateOpen(false)} />
      ) : null}
    </section>
  );
}

interface CreateModelModalProps {
  readonly onClose: () => void;
}

function CreateModelModal({ onClose }: CreateModelModalProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const create = useCreateModel();
  const [vendor, setVendor] = useState('');
  const [model, setModel] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  function closeIfIdle(): void {
    if (!create.isPending) {
      onClose();
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const trimmedVendor = vendor.trim();
    const trimmedModel = model.trim();
    const trimmedDisplayName = displayName.trim();
    if (trimmedVendor.length === 0 || trimmedModel.length === 0) {
      setError(t('models.create.error.vendor_model_required'));
      return;
    }
    const body: CreateModelRequest = {
      vendor: trimmedVendor,
      model: trimmedModel,
    };
    if (trimmedDisplayName.length > 0) {
      body.display_name = trimmedDisplayName;
    }
    setError(null);
    try {
      await create.mutateAsync(body);
      toast.success(t('models.create.toast.created', { model: `${trimmedVendor}/${trimmedModel}` }));
      onClose();
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) closeIfIdle();
      }}
      title={t('models.create.title')}
      description={t('models.create.subtitle')}
      closeLabel={t('common.close')}
      testId="model-create-modal"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label={t('models.create.vendor.label')} fieldId="create-model-vendor">
            <Input
              id="create-model-vendor"
              mono
              value={vendor}
              onChange={(event) => setVendor(event.currentTarget.value)}
              data-testid="create-model-vendor"
              autoComplete="off"
              placeholder={t('models.create.vendor.placeholder')}
            />
          </FormField>
          <FormField label={t('models.create.model.label')} fieldId="create-model-model">
            <Input
              id="create-model-model"
              mono
              value={model}
              onChange={(event) => setModel(event.currentTarget.value)}
              data-testid="create-model-model"
              autoComplete="off"
              placeholder={t('models.create.model.placeholder')}
            />
          </FormField>
          <FormField
            label={t('models.create.display_name.label')}
            fieldId="create-model-display-name"
            className="sm:col-span-2"
          >
            <Input
              id="create-model-display-name"
              value={displayName}
              onChange={(event) => setDisplayName(event.currentTarget.value)}
              data-testid="create-model-display-name"
              autoComplete="off"
              placeholder={t('models.create.display_name.placeholder')}
            />
          </FormField>
        </div>

        {error !== null ? (
          <p className="m-0 text-label text-bad" role="alert" data-testid="model-create-error">
            {error}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={closeIfIdle} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={create.isPending}
            data-testid="create-model-submit"
          >
            {create.isPending ? t('common.saving') : t('models.create.submit')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

interface DeleteModalProps {
  readonly target: DeleteTarget;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function DeleteModelModal({
  target,
  busy,
  error,
  onCancel,
  onConfirm,
}: DeleteModalProps): JSX.Element {
  const t = useT();
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title={t('models.delete_modal.title')}
      closeLabel={t('common.close')}
      size="sm"
      testId="model-delete-confirm"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            loading={busy}
            data-testid="model-delete-submit"
          >
            {busy ? t('common.deleting') : t('common.delete')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-body text-paper-mid">
          <span className="font-mono font-semibold text-paper">
            {target.vendor}/{target.model}
          </span>
          {t('models.delete_modal.body_suffix')}
          <code className="font-mono">{t('models.delete_modal.body_deleted_at_code')}</code>
          {t('models.delete_modal.body_tail')}
        </p>
        {error !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="model-delete-error"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
